import { Button, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { Document, Edit, TrashCan } from '@carbon/icons-react';
import { Loading, Modal } from '@carbon/react';
import { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import InvoiceDetailsForm, {
    emptyInvoiceDetailsForm,
    hasAnyInvoiceDetailsValue,
    hasCompleteRequiredInvoiceDetails,
    InvoiceDetailsFormValues,
    toInvoiceDetailsFormValues,
    toInvoiceDetailsUpsertDto,
} from 'Components/Common/InvoiceDetailsForm/InvoiceDetailsForm';
import sharedMessages from 'Components/utils/sharedMessages';
import {
    useCreateInvoiceDetailsMutation,
    useDeleteInvoiceDetailsMutation,
    useGetInvoiceDetailsQuery,
    useUpdateInvoiceDetailsMutation,
} from 'api/invoiceDetailsEndpoints';

import styles from './InvoiceDetails.module.scss';

const isNotFoundError = (error: unknown) => (
    error !== null
    && error !== undefined
    && typeof error === 'object'
    && 'status' in error
    && (error as FetchBaseQueryError).status === 404
);

const InvoiceValue = ({ label, value }: { label: string; value?: string | null }) => (
    <div>
        <Text size="xs" c="dimmed">
            {label}
        </Text>
        <Text size="sm">
            {value || '-'}
        </Text>
    </div>
);

const invoiceFormFields: Array<keyof InvoiceDetailsFormValues> = [
    'fullName',
    'invoiceEmail',
    'country',
    'street',
    'buildingNumber',
    'apartmentNumber',
    'postalCode',
    'voivodeship',
    'city',
    'companyName',
    'taxId',
    'euVatId',
];

const areInvoiceFormsEqual = (
    first: InvoiceDetailsFormValues,
    second: InvoiceDetailsFormValues,
) => invoiceFormFields.every((field) => first[field].trim() === second[field].trim());

export const InvoiceDetails = () => {
    const intl = useIntl();
    const {
        data: invoiceDetails,
        error,
        isFetching,
        isError,
        refetch,
    } = useGetInvoiceDetailsQuery();
    const [createInvoiceDetails, { isLoading: isCreating }] = useCreateInvoiceDetailsMutation();
    const [updateInvoiceDetails, { isLoading: isUpdating }] = useUpdateInvoiceDetailsMutation();
    const [deleteInvoiceDetails, { isLoading: isDeleting }] = useDeleteInvoiceDetailsMutation();
    const [formValues, setFormValues] = useState<InvoiceDetailsFormValues>(emptyInvoiceDetailsForm);
    const [initialFormValues, setInitialFormValues] = useState<InvoiceDetailsFormValues>(emptyInvoiceDetailsForm);
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const detailsFilled = Boolean(invoiceDetails);
    const isMissingDetails = isNotFoundError(error);
    const isSubmitting = isCreating || isUpdating;

    const displayedDetails = useMemo(() => toInvoiceDetailsFormValues(invoiceDetails), [invoiceDetails]);
    const isSaveDisabled = useMemo(
        () => (
            isSubmitting
            || !hasAnyInvoiceDetailsValue(formValues)
            || !hasCompleteRequiredInvoiceDetails(formValues)
            || areInvoiceFormsEqual(formValues, initialFormValues)
        ),
        [formValues, initialFormValues, isSubmitting],
    );

    const onOpenModal = () => {
        const nextFormValues = toInvoiceDetailsFormValues(invoiceDetails);
        setFormValues(nextFormValues);
        setInitialFormValues(nextFormValues);
        setSubmitAttempted(false);
        setModalOpen(true);
    };

    const onCloseModal = () => {
        setModalOpen(false);
    };

    const onSubmit = async () => {
        if (!hasCompleteRequiredInvoiceDetails(formValues)) {
            setSubmitAttempted(true);
            return;
        }

        const request = toInvoiceDetailsUpsertDto(formValues);

        if (invoiceDetails) {
            await updateInvoiceDetails(request).unwrap();
        } else {
            await createInvoiceDetails(request).unwrap();
        }

        setModalOpen(false);
    };

    const onOpenDeleteConfirmation = () => {
        setDeleteConfirmationOpen(true);
    };

    const onCloseDeleteConfirmation = () => {
        setDeleteConfirmationOpen(false);
    };

    const onDelete = async () => {
        await deleteInvoiceDetails().unwrap();
        setDeleteConfirmationOpen(false);
    };

    const updateField = (field: keyof InvoiceDetailsFormValues, value: string) => {
        setSubmitAttempted(false);
        setFormValues(previous => ({
            ...previous,
            [field]: value,
        }));
    };

    if (isFetching) {
        return (
            <Paper shadow="sm" p="xl" radius="md" withBorder className={styles.card}>
                <Loading />
            </Paper>
        );
    }

    if (isError && !isMissingDetails) {
        return <ErrorActionState onClick={refetch} />;
    }

    return (
        <>
            <Paper shadow="sm" p="xl" radius="md" withBorder className={styles.card}>
                <Group justify="space-between" align="center" mb="md">
                    <Group gap="xs">
                        <div className={styles.titleIconFrame}>
                            <Document size={24} />
                        </div>
                        <Title order={2} size="h3">
                            {intl.formatMessage(messages.invoiceDetails)}
                        </Title>
                    </Group>

                    {detailsFilled && (
                        <Group gap="xs">
                            <Button variant="subtle" leftSection={<Edit size={16} />} onClick={onOpenModal}>
                                {intl.formatMessage(messages.editDetails)}
                            </Button>
                            <Button
                                variant="subtle"
                                color="red"
                                leftSection={<TrashCan size={16} />}
                                onClick={onOpenDeleteConfirmation}
                            >
                                {intl.formatMessage(messages.deleteDetails)}
                            </Button>
                        </Group>
                    )}
                </Group>

                {!detailsFilled && (
                    <Stack align="center" justify="center" gap="md" className={styles.emptyState}>
                        <Text ta="center" c="dimmed">
                            {intl.formatMessage(messages.emptyState)}
                        </Text>
                        <Button fullWidth className={styles.fillButton} onClick={onOpenModal}>
                            {intl.formatMessage(messages.fillDetails)}
                        </Button>
                    </Stack>
                )}

                {detailsFilled && (
                    <Stack gap="lg">
                        <Stack gap="sm">
                            <Text size="sm" fw={700}>
                                {intl.formatMessage(messages.basicDetails)}
                            </Text>
                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                                <InvoiceValue label={intl.formatMessage(messages.fullName)} value={displayedDetails.fullName} />
                                <InvoiceValue label={intl.formatMessage(messages.invoiceEmail)} value={displayedDetails.invoiceEmail} />
                                <InvoiceValue label={intl.formatMessage(messages.country)} value={displayedDetails.country} />
                            </SimpleGrid>
                        </Stack>

                        <Stack gap="sm">
                            <Text size="sm" fw={700}>
                                {intl.formatMessage(messages.addressDetails)}
                            </Text>
                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                                <InvoiceValue label={intl.formatMessage(messages.street)} value={displayedDetails.street} />
                                <InvoiceValue label={intl.formatMessage(messages.buildingNumber)} value={displayedDetails.buildingNumber} />
                                <InvoiceValue label={intl.formatMessage(messages.apartmentNumber)} value={displayedDetails.apartmentNumber} />
                                <InvoiceValue label={intl.formatMessage(messages.postalCode)} value={displayedDetails.postalCode} />
                                <InvoiceValue label={intl.formatMessage(messages.voivodeship)} value={displayedDetails.voivodeship} />
                                <InvoiceValue label={intl.formatMessage(messages.city)} value={displayedDetails.city} />
                                <InvoiceValue label={intl.formatMessage(messages.country)} value={displayedDetails.country} />
                            </SimpleGrid>
                        </Stack>

                        <Stack gap="sm">
                            <Text size="sm" fw={700}>
                                {intl.formatMessage(messages.companyDetails)}
                            </Text>
                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                                <InvoiceValue label={intl.formatMessage(messages.companyName)} value={displayedDetails.companyName} />
                                <InvoiceValue label={intl.formatMessage(messages.taxId)} value={displayedDetails.taxId} />
                                <InvoiceValue label={intl.formatMessage(messages.euVatId)} value={displayedDetails.euVatId} />
                            </SimpleGrid>
                        </Stack>
                    </Stack>
                )}
            </Paper>

            <Modal
                open={modalOpen}
                onRequestClose={onCloseModal}
                onRequestSubmit={onSubmit}
                primaryButtonDisabled={isSaveDisabled}
                loadingStatus={isSubmitting ? 'active' : undefined}
                modalHeading={intl.formatMessage(messages.invoiceDetails)}
                modalLabel={intl.formatMessage(messages.modalLabel)}
                primaryButtonText={intl.formatMessage(messages.save)}
                secondaryButtonText={intl.formatMessage(messages.cancel)}
                closeButtonLabel={intl.formatMessage(messages.cancel)}
                size="lg"
            >
                <div className={styles.modalContent}>
                    <InvoiceDetailsForm
                        values={formValues}
                        onChange={updateField}
                        showValidationErrors={submitAttempted}
                    />
                </div>
            </Modal>

            <Modal
                open={deleteConfirmationOpen}
                onRequestClose={onCloseDeleteConfirmation}
                onRequestSubmit={onDelete}
                danger
                primaryButtonDisabled={isDeleting}
                loadingStatus={isDeleting ? 'active' : undefined}
                modalHeading={intl.formatMessage(messages.deleteConfirmationHeading)}
                modalLabel={intl.formatMessage(sharedMessages.irreversibleAction)}
                primaryButtonText={intl.formatMessage(sharedMessages.remove)}
                closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
                secondaryButtonText={intl.formatMessage(sharedMessages.back)}
            />
        </>
    );
};

const messages = defineMessages({
    invoiceDetails: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.invoiceDetails',
        defaultMessage: 'Dane do faktur',
    },
    editDetails: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.editDetails',
        defaultMessage: 'Edytuj dane',
    },
    deleteDetails: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.deleteDetails',
        defaultMessage: 'Usuń dane',
    },
    deleteConfirmationHeading: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.deleteConfirmationHeading',
        defaultMessage: 'Czy na pewno chcesz usunąć dane do faktury?',
    },
    emptyState: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.emptyState',
        defaultMessage: 'Jeżeli chcesz abyśmy wygenerowali dla Ciebie fakturę - uzupełnij dane',
    },
    fillDetails: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.fillDetails',
        defaultMessage: 'Uzupełnij dane',
    },
    basicDetails: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.basicDetails',
        defaultMessage: 'Dane podstawowe',
    },
    fullName: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.fullName',
        defaultMessage: 'Imię i nazwisko',
    },
    fullNamePrivate: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.fullNamePrivate',
        defaultMessage: 'Imię i nazwisko (dla osoby prywatnej)',
    },
    invoiceEmail: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.invoiceEmail',
        defaultMessage: 'Adres e-mail do faktury',
    },
    country: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.country',
        defaultMessage: 'Kraj',
    },
    addressDetails: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.addressDetails',
        defaultMessage: 'Dane adresowe',
    },
    street: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.street',
        defaultMessage: 'Ulica',
    },
    buildingNumber: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.buildingNumber',
        defaultMessage: 'Numer budynku',
    },
    apartmentNumber: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.apartmentNumber',
        defaultMessage: 'Numer lokalu',
    },
    postalCode: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.postalCode',
        defaultMessage: 'Kod pocztowy',
    },
    voivodeship: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.voivodeship',
        defaultMessage: 'Województwo',
    },
    city: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.city',
        defaultMessage: 'Miasto',
    },
    companyDetails: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.companyDetails',
        defaultMessage: 'Dane firmy',
    },
    companyName: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.companyName',
        defaultMessage: 'Nazwa firmy',
    },
    taxId: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.taxId',
        defaultMessage: 'NIP',
    },
    euVatId: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.euVatId',
        defaultMessage: 'VAT UE',
    },
    euVatIdOptional: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.euVatIdOptional',
        defaultMessage: 'VAT UE (opcjonalnie, dla firm z UE)',
    },
    modalLabel: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.modalLabel',
        defaultMessage: 'Uzupełnij dane potrzebne do wygenerowania faktury',
    },
    save: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.save',
        defaultMessage: 'Zapisz',
    },
    cancel: {
        id: 'src.Screens.Dashboard.Subscription.InvoiceDetails.cancel',
        defaultMessage: 'Anuluj',
    },
});
